import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Check, Swords, Palette } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserBits";

export const skinColors = (d) => {
  const pick = (id) => d?.skins?.find((x) => x.id === id)?.colors;
  return { heart: pick(d?.skin?.heart), bg: pick(d?.skin?.bg) };
};

const Swatch = ({ k }) => k.kind === "heart"
  ? <span className="flex gap-1 justify-center">{k.colors.map((c) => <span key={c} className="w-5 h-5 rounded-full shadow" style={{ background: c, boxShadow: `0 0 10px ${c}` }} />)}</span>
  : <span className="block h-6 w-full rounded-lg" style={{ background: `linear-gradient(180deg, ${k.colors[1]}, ${k.colors[0]})` }} />;

export function SkinShop({ d, reload }) {
  const { user, refreshUser } = useAuth();
  const [busy, setBusy] = useState("");
  const act = async (k) => {
    setBusy(k.id);
    try {
      if (!k.owned) { await api.post(`/twohearts/skins/${k.id}/buy`); toast.success(`„${k.name}“ freigeschaltet ✨`); refreshUser?.(); }
      else await api.post(`/twohearts/skins/${k.equipped ? `none:${k.kind}` : k.id}/equip`);
      await reload();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); }
  };
  return (
    <div data-testid="twohearts-skins">
      <p className="text-xs font-extrabold text-white/60 mb-2 flex items-center gap-1"><Palette className="w-3.5 h-3.5" /> SKINS · dein Guthaben: {user?.points ?? 0} Punkte</p>
      <div className="grid grid-cols-3 gap-2">
        {d.skins.map((k) => (
          <button key={k.id} onClick={() => act(k)} disabled={busy === k.id} data-testid={`twohearts-skin-${k.id}`} data-owned={k.owned} data-equipped={k.equipped}
            className={`rounded-2xl p-2.5 text-center space-y-1.5 transition-transform active:scale-95 ${k.equipped ? "bg-pink-500/30 ring-2 ring-pink-400" : "bg-white/10"}`}>
            <Swatch k={k} />
            <span className="block text-[11px] font-bold leading-tight">{k.name}</span>
            <span className="block text-[10px] font-extrabold text-white/70">
              {k.equipped ? <><Check className="w-3 h-3 inline" /> Aktiv</> : k.owned ? "Benutzen" : <><Lock className="w-3 h-3 inline" /> {k.price} P</>}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function DuelPanel() {
  const [d, setD] = useState(null);
  const [fid, setFid] = useState("");
  const navigate = useNavigate();
  useEffect(() => { api.get("/twohearts/duels").then(({ data }) => setD(data)).catch(() => setD({ invites: [], friends: [] })); }, []);
  const challenge = async () => {
    try { const { data } = await api.post("/twohearts/duel", { friend_id: fid }); navigate(`/zwei-herzen/duell/${data.id}`); }
    catch (e) { toast.error(formatErr(e)); }
  };
  if (!d) return null;
  return (
    <div className="rounded-3xl bg-white/10 p-4 space-y-3" data-testid="twohearts-duel-panel">
      <p className="font-black flex items-center gap-2"><Swords className="w-5 h-5 text-pink-300" /> Herzen-Duell</p>
      <p className="text-xs text-white/70">Ihr spielt gleichzeitig dieselbe Strecke und seht live, wer weiter kommt. Der Sieger bekommt +5 Punkte.</p>
      {d.invites.map((i) => (
        <div key={i.id} className="flex items-center gap-3 rounded-2xl bg-pink-500/20 p-2.5" data-testid={`twohearts-invite-${i.id}`}>
          <UserAvatar name={i.opp.user?.name} picture={i.opp.user?.picture} size="w-9 h-9" noStory />
          <span className="flex-1 text-sm font-bold">{i.opp.user?.name?.split(" ")[0]} fordert dich heraus!</span>
          <button onClick={() => navigate(`/zwei-herzen/duell/${i.id}`)} data-testid={`twohearts-invite-open-${i.id}`} className="h-9 px-4 rounded-full bg-pink-500 text-sm font-black">Ansehen</button>
        </div>
      ))}
      {d.friends.length ? (
        <div className="flex gap-2">
          <select value={fid} onChange={(e) => setFid(e.target.value)} data-testid="twohearts-duel-friend" className="flex-1 min-w-0 h-11 rounded-full bg-white/10 px-4 text-sm font-bold text-white">
            <option value="" className="text-slate-900">Freund wählen …</option>
            {d.friends.map((f) => <option key={f.user_id} value={f.user_id} className="text-slate-900">{f.name}</option>)}
          </select>
          <button onClick={challenge} disabled={!fid} data-testid="twohearts-duel-start" className="h-11 px-5 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 font-black disabled:opacity-50">Duell</button>
        </div>
      ) : <p className="text-xs text-white/60" data-testid="twohearts-duel-nofriends">Füge zuerst Freunde hinzu, um sie herauszufordern.</p>}
    </div>
  );
}
